import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent, TextField, Typography } from "@mui/material"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { ADJUSTMENT_TYPE_METADATA, ADJUSTMENT_TYPE_OPTIONS } from "../constants"
import { type AdjustmentLaunchFormValues, adjustmentLaunchFormSchema } from "../schemas"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { InventoryAdjustmentType, type Product } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface AdjustmentLaunchFormDialogProps {
  product: Product
  warehouseOptions: Option<string>[]
  onClose: () => void
  onContinue: (formValues: AdjustmentLaunchFormValues) => void
}

export const AdjustmentLaunchFormDialog: FC<AdjustmentLaunchFormDialogProps> = ({
  product,
  warehouseOptions,
  onClose,
  onContinue,
}) => {
  const form = useForm<AdjustmentLaunchFormValues>({
    resolver: zodResolver(adjustmentLaunchFormSchema),
    defaultValues: {
      warehouseAreaId: warehouseOptions[0]?.value ?? "",
      adjustmentType: InventoryAdjustmentType.AdjustQuantity,
    },
  })

  const { control, handleSubmit, watch } = form
  const selectedAdjustmentType = watch("adjustmentType")

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { minWidth: 560 } }}
      data-testid='inventory.adjustment-launch-form-dialog'
    >
      <DialogHeader title='Inventory Adjustment & Pallet Operations' onClose={onClose} />
      <FormProvider {...form}>
        <DialogContent className='flex flex-col gap-y-6 p-10'>
          <TextField
            label='Product'
            value={product.companyProductDescription}
            disabled
            variant='outlined'
            data-testid='inventory.adjustment-launch-form-dialog.product-field'
          />
          <FormSelect
            control={control}
            name='warehouseAreaId'
            label='Warehouse'
            options={warehouseOptions}
            data-testid='inventory.adjustment-launch-form-dialog.warehouse-field'
          />
          <Box>
            <FormSelect
              control={control}
              name='adjustmentType'
              label='Adjustment Type'
              required
              options={ADJUSTMENT_TYPE_OPTIONS}
              data-testid='inventory.adjustment-launch-form-dialog.adjustment-type-field'
            />
            <Typography variant='body2' className='mt-2 text-gray-700'>
              {ADJUSTMENT_TYPE_METADATA[selectedAdjustmentType].description}
            </Typography>
          </Box>
        </DialogContent>
      </FormProvider>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button
          appearance='outlined'
          onClick={onClose}
          data-testid='inventory.adjustment-launch-form-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          onClick={handleSubmit(onContinue)}
          data-testid='inventory.adjustment-launch-form-dialog.continue-button'
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  )
}
